import React, { useState, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { usePrivateDealAssignment } from '../hooks/usePrivateDealAssignment';
import UserSelect from './UserSelect';

// dealId / dealIsPrivate: optional deal context for private-deal assignment rules —
// callers pass them only when every selected risk belongs to the same deal. When the
// deal is private, non-owner/admin assigners only see deal members; the deal's
// owner/admin sees everyone (non-members tagged) and can grant Contributor access on
// the fly. Callers without deal context omit both — behavior unchanged.
const BulkAssignRiskModal = ({ show, onClose, risks, riskType = 'Deals', refreshData, dealId, dealIsPrivate }) => {
    const apiCall = useApi();
    const { user } = useAuth();

    const [users, setUsers]                   = useState([]);
    const [loadingUsers, setLoadingUsers]     = useState(true);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [targetDate, setTargetDate]         = useState('');
    const [lastInviteData, setLastInviteData] = useState(null);
    const [isProcessing, setIsProcessing]     = useState(false);
    const [progress, setProgress]             = useState(null); // { done, total, failed }
    const [done, setDone]                     = useState(false);

    const today = new Date().toISOString().split('T')[0];

    // Private-deal assignment rules (inert when no dealId / deal is public)
    const { isPrivateDeal, memberIds, canManagePrivateDeal, grantDealContributor } =
        usePrivateDealAssignment(show, dealId, dealIsPrivate);

    useEffect(() => {
        if (!show) return;
        setSelectedUserId('');
        setTargetDate('');
        setProgress(null);
        setDone(false);
        setLoadingUsers(true);
        apiCall('GET', '/users')
            .then(res => setUsers(res || []))
            .catch(() => {})
            .finally(() => setLoadingUsers(false));
    }, [show]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!show) return null;

    const resolveEndpoint = (risk) => {
        // All risks are mutated via the unified controller (GUID id).
        return `/unifiedrisks/${risk.id || risk.riskId}/assign`;
    };

    const resolvePayload = (risk, userId, isInvite) => {
        const dt = targetDate ? new Date(targetDate).toISOString() : null;
        // The unified assign endpoint expects the generic userId/userInviteId shape.
        return {
            userId:                isInvite ? null : (parseInt(userId) || null),
            userInviteId:          isInvite ? parseInt(String(userId).replace('invite:', '')) : null,
            remediationTargetDate: dt,
        };
    };

    const handleAssign = async () => {
        if (!selectedUserId || !targetDate) return;

        const isInvite = String(selectedUserId).startsWith('invite:');
        const found    = users.find(u => String(u.userId) === String(selectedUserId) || String(u.id) === String(selectedUserId));
        const assigneeName = isInvite && lastInviteData
            ? (lastInviteData.fullName || lastInviteData.email)
            : found ? (found.fullName || found.email) : `User #${selectedUserId}`;

        // Private deal + owner/admin assigning a non-member: confirm, then grant
        // CONTRIBUTOR@DEAL once before the loop (the backend rejects non-members).
        if (isPrivateDeal && canManagePrivateDeal && !isInvite && memberIds
            && !memberIds.has(parseInt(selectedUserId))) {
            const confirmed = window.confirm(
                `${assigneeName} can't see this private deal. Grant Contributor access and assign?`
            );
            if (!confirmed) return;
            try {
                await grantDealContributor(parseInt(selectedUserId));
            } catch {
                setProgress({ done: 0, total: risks.length, failed: risks.length,
                    failedTitles: ['Could not grant deal access — no risks were assigned.'] });
                setDone(true);
                return;
            }
        }

        setIsProcessing(true);
        setProgress({ done: 0, total: risks.length, failed: 0, failedTitles: [] });

        let doneCount   = 0;
        let failedCount = 0;
        const failedTitles = [];

        // Process sequentially to respect server rate limits and EF Core context safety
        for (const risk of risks) {
            try {
                const payload = resolvePayload(risk, selectedUserId, isInvite);
                await apiCall('PUT', resolveEndpoint(risk), payload);

                // Activity log
                try {
                    await apiCall('POST', '/allRemediations/logs/by-risk', {
                        remediationType: riskType || risk.riskType || risk.remediationType,
                        riskId:          risk.id || risk.riskId,
                        previousStatus:  null,
                        newStatus:       assigneeName,
                        comment:         `Risk assigned to ${assigneeName} (bulk assignment)`,
                        changedByUserName: user?.userName || user?.email || '',
                        changeType:      'Assign',
                        targetDate:      targetDate ? new Date(targetDate).toISOString() : null,
                    });
                } catch { /* log failure is non-fatal */ }

                doneCount++;
            } catch {
                failedCount++;
                failedTitles.push(risk.db_RiskTitle || risk.riskTitle || risk.remediationTitle || `Risk #${risk.id || risk.riskId}`);
            }
            setProgress({ done: doneCount + failedCount, total: risks.length, failed: failedCount, failedTitles });
        }

        setIsProcessing(false);
        setDone(true);
        if (doneCount > 0) refreshData();
    };

    const assigneeName = (() => {
        if (!selectedUserId) return null;
        if (String(selectedUserId).startsWith('invite:') && lastInviteData)
            return lastInviteData.fullName || lastInviteData.email;
        const found = users.find(u => String(u.userId) === String(selectedUserId) || String(u.id) === String(selectedUserId));
        return found ? (found.fullName || found.email) : null;
    })();

    const canSubmit = !isProcessing && !done && !!selectedUserId && !!targetDate && targetDate >= today;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-16 mx-auto p-6 border w-full max-w-lg shadow-xl rounded-lg bg-white">

                {/* Header */}
                <div className="flex items-center justify-between mb-5">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            Assign {risks.length} Risk{risks.length !== 1 ? 's' : ''}
                        </h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                            All selected risks will be assigned to the same person with the same target date.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 ml-4">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Risk list preview */}
                <div className="mb-5 max-h-36 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 divide-y divide-gray-100">
                    {risks.map(risk => {
                        const sev = (risk.userRating || risk.riskSeverity || risk.residualRiskRating || 'LOW').toUpperCase();
                        const sevColor = sev === 'CRITICAL' ? 'text-red-700 bg-red-100' :
                                         sev === 'HIGH'     ? 'text-orange-700 bg-orange-100' :
                                         sev === 'MEDIUM'   ? 'text-yellow-700 bg-yellow-100' :
                                                              'text-green-700 bg-green-100';
                        return (
                            <div key={risk.id || risk.riskId} className="flex items-center gap-2 px-3 py-2 text-sm">
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${sevColor}`}>{sev}</span>
                                <span className="text-gray-700 truncate">{risk.db_RiskTitle || risk.riskTitle || risk.remediationTitle}</span>
                            </div>
                        );
                    })}
                </div>

                {/* Form — hidden while processing / done */}
                {!done && (
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">Assign To</label>
                            <UserSelect
                                value={selectedUserId}
                                onChange={setSelectedUserId}
                                users={(isPrivateDeal && !canManagePrivateDeal && memberIds)
                                    ? users.filter(u => memberIds.has(u.id))
                                    : users}
                                loading={loadingUsers}
                                canInvite={!isPrivateDeal || canManagePrivateDeal}
                                nonMemberIds={(isPrivateDeal && canManagePrivateDeal && memberIds)
                                    ? new Set(users.filter(u => !memberIds.has(u.id)).map(u => u.id))
                                    : undefined}
                                onInviteSuccess={d => setLastInviteData(d)}
                            />
                            {isPrivateDeal && !canManagePrivateDeal && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Only the deal owner can invite external users to a private deal.
                                </p>
                            )}
                            {isPrivateDeal && canManagePrivateDeal && (
                                <p className="text-xs text-gray-500 mt-1">
                                    Invited users will be granted access to this private deal when they join.
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                                Target Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={targetDate}
                                min={today}
                                onChange={e => setTargetDate(e.target.value)}
                                className={`w-full border rounded-md px-3 py-2 text-sm ${!targetDate ? 'border-red-300' : 'border-gray-300'}`}
                            />
                            {!targetDate && <p className="text-xs text-red-500 mt-1">Required</p>}
                        </div>
                    </div>
                )}

                {/* Progress bar */}
                {isProcessing && progress && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                            <span className="flex items-center gap-1">
                                <Loader className="h-3 w-3 animate-spin" /> Assigning…
                            </span>
                            <span>{progress.done} / {progress.total}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-blue-500 h-2 rounded-full transition-all"
                                style={{ width: `${(progress.done / progress.total) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Result */}
                {done && progress && (
                    <div className="mt-4 space-y-2">
                        {progress.done - progress.failed > 0 && (
                            <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                                <CheckCircle className="h-4 w-4 flex-shrink-0" />
                                {progress.done - progress.failed} risk{progress.done - progress.failed !== 1 ? 's' : ''} assigned to {assigneeName}
                            </div>
                        )}
                        {progress.failed > 0 && (
                            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                                <div className="flex items-center gap-2 mb-1">
                                    <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                    {progress.failed} risk{progress.failed !== 1 ? 's' : ''} failed to assign
                                </div>
                                <ul className="ml-6 list-disc text-xs space-y-0.5 text-red-600">
                                    {progress.failedTitles.map((t, i) => <li key={i}>{t}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                {/* Buttons */}
                <div className="flex justify-end gap-3 mt-6">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                        {done ? 'Close' : 'Cancel'}
                    </button>
                    {!done && (
                        <button
                            onClick={handleAssign}
                            disabled={!canSubmit}
                            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isProcessing && <Loader className="h-4 w-4 animate-spin" />}
                            {isProcessing ? 'Assigning…' : `Assign ${risks.length} Risk${risks.length !== 1 ? 's' : ''}`}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BulkAssignRiskModal;
